import { TableColumn } from "@components/Table/TableColumns";
import { SortingOption } from "@components/Table/TableSorting";
import { HistoricTrendTableItem } from "@core/api/useHistoricTrend";
import { Button, ButtonType, getRem, markMatch, Tooltip } from "@datassential/platform-ui-lib";
import { useCallback, useMemo } from "react";
import { useHistoricTrendContext } from "./HtContext";
import { IoArrowUp, IoClose } from "react-icons/io5";
import { CHART_COLORS } from "@core/constants";
import { useTrendContext } from "@pages/Trend/TrendContext";
import Popup from "@components/Popup";

export const NAME_COL_WIDTH = 300;
export const NUM_COL_WIDTH = 120;

export function useHistoricTrendTableConfig(
	data: HistoricTrendTableItem[],
	surveyIds: SortingOption[],
	hideSorting: boolean = false,
) {
	const { search } = useTrendContext();

	return useMemo(
		(): TableColumn[] => [
			{
				key: 'name',
				sortKey: hideSorting ? undefined : 'name',
				head: 'Item',
				headText: 'Item',
				Value: ({ index }) => {
					const {
						selectedItems,
						setSelectedItems,
					} = useHistoricTrendContext();

					const selectedItem = useMemo(() => {
						return selectedItems.find((item) => item.id === data[index]?.id);
					}, [selectedItems, index]);

					const isDisabled = useMemo(() => {
						return !selectedItem && selectedItems.length >= 5;
					}, [selectedItem, selectedItems]);

					const onButtonClick = useCallback(() => {
						if (selectedItem) {
							setSelectedItems(selectedItems.filter((item) => item.id !== data[index]?.id));
						} else {
							if (selectedItems.length >= 5) return;

							const usedColors = selectedItems.map((item) => item.color);
							const remainingColors = CHART_COLORS.filter((color) => !usedColors.includes(color));

							setSelectedItems([...selectedItems, {
								id: data[index].id ?? 0,
								color: remainingColors[0],
							}]);
						}
					}, [selectedItems, index, setSelectedItems, selectedItem]);

					const isSelected = useMemo(
						() => selectedItem !== undefined,
						[selectedItem]
					);

					const tooltipText = useMemo(
						() => {
							if (isSelected) {
								return 'remove from chart';
							}
							else if (isDisabled) {
								return 'items per chart limit is 5';
							}
							else {
								return 'add to chart';
							}
						},
						[isSelected, isDisabled]
					);

					const tooltipId = useMemo(
						() => `ht-table-item-${data[index].id}`,
						[index]
					);

					return (
						<div className="HtTable__item-name">
							<div
								className="HtTable__item-name-button"
								data-tooltip-id={tooltipId}
							>
								<Button
									type={ButtonType.Button}
									onClick={onButtonClick}
									style={
										isSelected ? {
											background: selectedItem?.color,
											color: '#FCFCFC',
										} : {}
									}
									isDisabled={isDisabled}
									testId={`buttonHtTableItem_${index}`}
								>
									{selectedItem ? <IoClose/> : <IoArrowUp/>}
								</Button>
							</div>
							<span
								className="HtTable__item-name-text"
								data-testid="textHtTableItemName"
								dangerouslySetInnerHTML={{
									__html: search.trim() === ''
										? (data[index]?.name?.toUpperCase() ?? '')
										: markMatch(data[index]?.name?.toUpperCase() ?? '', search.trim(), true)
								}}
							/>
							<Popup>
								<Tooltip
									id={tooltipId}
									place="top-start"
								>
									{tooltipText}
								</Tooltip>
							</Popup>
						</div>
					)
				},
				valueText: (index) => data[index]?.name ?? '',
				style: {
					cell: {
						minWidth: getRem(NAME_COL_WIDTH),
						wordBreak: 'break-word',
						textOverflow: 'ellipsis',
						whiteSpace: 'pre-wrap',
						position: "sticky",
						left: 0,
						paddingLeft: getRem(16),
					},
					head: {
						zIndex: 3,
					}
				}
			},
			{
				key: 'average',
				sortKey: hideSorting ? undefined : 'averageNum',
				head: 'Average',
				headText: 'Average',
				Value: ({ index }) => <>{data[index]?.averageValue}</>,
				valueText: (index) => data[index]?.averageValue,
				style: {
					cell: {
						justifyContent: 'center',
						minWidth: getRem(NUM_COL_WIDTH),
						position: "sticky",
						left: getRem(NAME_COL_WIDTH),
					},
					head: {
						zIndex: 3,
					},
				}
			},
			{
				key: 'change',
				sortKey: hideSorting ? undefined : 'changeNum',
				head: '2 Yr Change',
				headText: '2 Yr Change',
				Value: ({ index }) => {


					return <>{data[index].changeValueSigned}</>
				},
				valueText: (index) => data[index].changeValue,
				style: {
					cell: {
						justifyContent: 'center',
						minWidth: getRem(NUM_COL_WIDTH),
						position: "sticky",
						left: getRem(NAME_COL_WIDTH + NUM_COL_WIDTH),
					},
					head: {
						zIndex: 3,
					},
				}
			},
			...surveyIds.map((survey) => {
				const surveyId = survey.id?.toString() ?? '';

				return {
					key: surveyId,
					sortKey: hideSorting ? undefined : survey.name,
					head: survey.name,
					headText: survey.name,
					Value: ({ index }) => {
						return <>{data[index]?.surveys.get(survey.id as number)?.value}</>
					},
					valueText: (index) => data[index]?.surveys.get(survey.id as number)?.value,
					style: {
						cell: {
							justifyContent: 'center',
							minWidth: getRem(NUM_COL_WIDTH),
						},
					}
				} as TableColumn;
			}),
		],
		[surveyIds, search, data, hideSorting]
	);
}